import { eurosPerMonthCapMsg, t } from '../../common/i18n';
import { eurosToCents, formatSumToString } from '../../common/utils/priceUtils.js';
import { getProductOfferingPriceDataWithFallback, isMandatoryAddon } from '../../common/utils/productOfferingsUtils.js';
import { ProductOfferingPrice } from '../../generated/api/productOfferingPrice.js';
import { ACCESSORY_IMAGE_URLS } from '../DeviceDetailsCoCo/DeviceDetailsAccessoryPictureCoCo.js';
import { getProductStockLevel } from '../DeviceDetailsCoCo/deviceDetailsUtilsCoCo.js';
import type { ProductOfferingWithSelection } from './ProductOfferingsModalCoCo.js';
import type { AddonProductOfferingWithSelection } from '../../common/types/productOfferings.js';
import type { ProductOfferingCartItems } from '../../common/types/shoppingBasketCoCo.js';
import type { BundleAddonGroup } from '../../generated/api/bundleAddonGroup.js';
import type { CartBundleAddonGroup } from '../../generated/api/cartBundleAddonGroup.js';
import type { ProductOfferingCharacteristic } from '../../generated/api/productOfferingCharacteristic.js';
import type { ProductOfferingsResponse } from '../../generated/api/productOfferingsResponse.js';
import type { OfferingWithStock } from '../DeviceDetailsCoCo/deviceDetailsUtilsCoCo.js';
import type { BundledAddonGroupSelection } from '../ProductOfferingAddonCoCo/BundleAddonGroupSelectorCoCo.js';

// Offerings without stock information are treated as available.
export const isOfferingInStock = (offering: OfferingWithStock): boolean => {
  const stockLevel = getProductStockLevel(offering);
  return stockLevel === undefined || stockLevel > 0;
};

export const getGroupOfferingPriceInEuros = (productOfferingPrice: ProductOfferingPrice[] | undefined): number =>
  getProductOfferingPriceDataWithFallback(
    productOfferingPrice,
    ProductOfferingPrice.PriceTypeEnum.OC,
    ProductOfferingPrice.PriceTypeEnum.RC
  )?.priceWithoutTax ?? 0;

export const getRecurringOfferingPriceInEuros = (productOfferingPrice: ProductOfferingPrice[] | undefined): number =>
  getProductOfferingPriceDataWithFallback(
    productOfferingPrice,
    ProductOfferingPrice.PriceTypeEnum.RC,
    ProductOfferingPrice.PriceTypeEnum.OC
  )?.priceWithoutTax ?? 0;

export const getDefaultOfferingPriceInEuros = (productOfferingPrice: ProductOfferingPrice[] | undefined): number =>
  productOfferingPrice?.[0]?.priceWithoutTax ?? 0;

const generateCartItemId = () => `cartItem-${crypto.randomUUID()}`;

const getBundledAddonProductOfferingAsPerCart = (
  bundledOfferings: ProductOfferingWithSelection[]
): AddonProductOfferingWithSelection[] => {
  return bundledOfferings.map(bundledOffering => ({
    id: bundledOffering.id,
    name: bundledOffering.name,
    cartItemId: generateCartItemId(),
    productOfferingType: bundledOffering.productOfferingType,
    productOfferingPrice: bundledOffering.productOfferingPrice,
    isChecked: bundledOffering.isChecked,
    isDefault: isMandatoryAddon(bundledOffering),
    description: bundledOffering.description ?? { value: '' },
  }));
};

const getCommercialAddonProductOfferingAsPerCart = (
  commercialOfferings: ProductOfferingWithSelection[]
): AddonProductOfferingWithSelection[] =>
  getBundledAddonProductOfferingAsPerCart(commercialOfferings).map((mapped, index) => ({
    ...mapped,
    productOfferingRelationship: commercialOfferings[index].productOfferingRelationship,
  }));

const getBundleAddonGroupsAsPerCart = (
  groups: BundleAddonGroup[],
  groupSelections: BundledAddonGroupSelection[]
): CartBundleAddonGroup[] =>
  groups.map(group => {
    const selection = groupSelections.find(s => s.groupId === group.id);
    return {
      id: group.id,
      name: group.name,
      description: group.description,
      cardinality: group.cardinality,
      productOffering: group.productOffering?.map(offering => ({
        id: offering.id,
        name: offering.name,
        cartItemId: generateCartItemId(),
        productOfferingType: offering.productOfferingType,
        productOfferingPrice: offering.productOfferingPrice,
        isDefault: offering.isDefault,
        cardinality: offering.cardinality,
        isSelected: selection?.selectedOfferingIds.includes(offering.id) ?? false,
        productSpecRuntimeCharacteristics: offering.productSpecRuntimeCharacteristics,
      })),
    };
  });

export const createCartItemFromProductOffering = (
  productOffer: ProductOfferingsResponse,
  quantity: number,
  bundledAddonOfferings?: ProductOfferingWithSelection[],
  groupSelections?: BundledAddonGroupSelection[],
  commercialAddonOfferings?: ProductOfferingWithSelection[],
  productOfferingRuntimeCharacteristic?: ProductOfferingCharacteristic[]
): ProductOfferingCartItems => ({
  id: productOffer.id,
  name: productOffer.name ?? '',
  description: productOffer.description ?? '',
  cartItemId: generateCartItemId(),
  quantity,
  imageUrl: ACCESSORY_IMAGE_URLS[productOffer.id],
  productOfferingType: productOffer.productOfferingType,
  productOfferingPrice: productOffer.productOfferingPrice ?? [],
  extensions: productOffer.extensions,
  planProductOffering: productOffer.planProductOffering
    ? {
        id: productOffer.planProductOffering.id,
        name: productOffer.planProductOffering.name,
        cartItemId: generateCartItemId(),
        productOfferingType: productOffer.planProductOffering.productOfferingType,
        productOfferingPrice: productOffer.planProductOffering.productOfferingPrice,
        // Kept so that the basket and checkout can render the selectable options and resolve their
        // labels without re-fetching the catalog.
        productOfferingCharacteristic: productOffer.planProductOffering.productOfferingCharacteristic,
        productOfferingRuntimeCharacteristic: productOffer.planProductOffering.productOfferingRuntimeCharacteristic,
        // Needed so DIRECTORY_LISTING_PUBLICITY (and PURPOSE_OF_USE) selections can be validated
        // against the catalog's own value[] options and merged back into the basket entry (see
        // `updatePlanProductOfferingSpecRuntimeCharacteristicInBasket` in `shoppingBasketHelpersCoCo.ts`).
        // Without this, the cart item never carries the catalog's characteristic definitions at all,
        // so `buildCharacteristicSelection` always finds nothing to match against and any basket
        // update for these characteristics silently no-ops regardless of what the user selects.
        productSpecRuntimeCharacteristics: productOffer.planProductOffering.productSpecRuntimeCharacteristics,
      }
    : undefined,
  bundledAddonProductOffering: [...getBundledAddonProductOfferingAsPerCart(bundledAddonOfferings ?? [])],
  commercialAddonProductOffering: getCommercialAddonProductOfferingAsPerCart(commercialAddonOfferings ?? []),
  bundleAddonGroup: getBundleAddonGroupsAsPerCart(productOffer.bundleAddonGroup ?? [], groupSelections ?? []),
  productOfferingRuntimeCharacteristic,
});

export const calculateTotalCheckedPrice = (offerings: ProductOfferingWithSelection[]): number =>
  offerings.reduce(
    (total, offering) =>
      offering.isChecked ? total + eurosToCents(getDefaultOfferingPriceInEuros(offering.productOfferingPrice)) : total,
    0
  );

export const getProductOfferAddOnPrices = (addOnPriceInEuros: number) => ({
  value: `${formatSumToString(eurosToCents(addOnPriceInEuros), true)} ${t.H37G(eurosPerMonthCapMsg)}`,
});

export const validateGroups = (
  bundleAddonGroups: BundleAddonGroup[],
  groupSelections: BundledAddonGroupSelection[]
): Record<string, string> => {
  const errors: Record<string, string> = {};
  for (const group of bundleAddonGroups) {
    const minNum = group.cardinality?.minNum ?? 0;
    const selection = groupSelections.find(s => s.groupId === group.id);
    const selectedCount = selection?.selectedOfferingIds.length ?? 0;
    // A group with nothing in stock has nothing to select and must not block the order.
    const offerings = group.productOffering ?? [];
    const hasNothingInStock = offerings.length > 0 && !offerings.some(isOfferingInStock);
    if (minNum > 0 && !hasNothingInStock && selectedCount < minNum) {
      errors[group.id] = `Please select at least ${minNum} addon${minNum > 1 ? 's' : ''}`;
    }
  }
  return errors;
};
